import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { Button, Icon, Loader } from "semantic-ui-react";

import { AdvisedCriticalHistory, ScrollToSectionMenu } from "components";
import { useScrollPageToTop } from "hooks";
import "modules/CarDetails/CarDetails.scss";
import { AppointmentHistory, CarDrivingTrend, CarNotes, CustomerInfo, Details, PinItems, SnoozedItems, Tyres } from "modules/CarDetails/components";
import { useAppointmentHistoryQuery, useHoldingAppointmentHistoryQuery, useRealTimeCarQuery, useScrollToSectionMenus } from "modules/CarDetails/hooks";
import { useRedirectToLandingPage } from "modules/Login/hooks";
import { NotFoundPage } from "router/components";
import { RESOURCE_NOT_FOUND } from "utils/Api";

export const CarDetails = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation();
  const id = Number(useParams<{ id: string }>().id);

  const [expanded, setExpanded] = useState<boolean>(true);
  const [isBackPressed, setIsBackPressed] = useState(false);

  const { car, loading, error } = useRealTimeCarQuery(id);
  const { appointments } = useAppointmentHistoryQuery(car?.id);
  const { holdingAppointments } = useHoldingAppointmentHistoryQuery(car?.id);
  const redirectToLandingPage = useRedirectToLandingPage();
  const sectionMenus = useScrollToSectionMenus({ car, appointments, holdingAppointments });
  useScrollPageToTop();

  if (loading)
    return (
      <div className="CarDetails-loader">
        <Loader active inline />
      </div>
    );

  if (error?.message.includes(RESOURCE_NOT_FOUND)) return <NotFoundPage />;

  if (!car) return;

  const toggleExpand = () => setExpanded(prev => !prev);

  const navigateBack = () => {
    setIsBackPressed(true);

    const urlSearchParams = new URLSearchParams(location.search);
    const fromAppointment = urlSearchParams.get("from");

    if (window.history.length > 1) fromAppointment ? history.replace(fromAppointment) : history.goBack();
    else redirectToLandingPage();
    setIsBackPressed(false);
  };

  const latestAppointment = appointments?.at(0);

  return (
    <>
      <div className="carDetail-container">
        <div className="carDetail-NavigationHeader">
          <div className="carDetail-NavigationHeader-container">
            <div className="row">
              <ScrollToSectionMenu sectionMenus={sectionMenus} offset={-125} hideBorder />
              <div className="sub-row">
                <Button className="-appointment-status icon" size="small" onClick={toggleExpand}>
                  <Icon className={`arrows ${expanded ? "minimize" : "maximize"}`} color="green" />
                </Button>
                <Button className="-appointment-status" size="small" active loading={isBackPressed} disabled={isBackPressed} onClick={navigateBack}>
                  <Icon className="angle left" />
                  {t("v8_back", "Back")}
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="CarDetails-cards mt-20">
          <Details car={car} sectionMenu={sectionMenus.details} />
          <CarNotes car={car} sectionMenu={sectionMenus.carNotes} isExpanded={expanded} />
          <CustomerInfo latestAppointmentId={latestAppointment?.id} sectionMenu={sectionMenus.customerInfo} isExpanded={expanded} />
          <SnoozedItems car_id={car.id} sectionMenu={sectionMenus.snoozedItems} isExpanded={expanded} />
          <PinItems car={car} sectionMenu={sectionMenus.pinnedItems} isExpanded={expanded} />
          <AdvisedCriticalHistory carId={car.id} sectionMenu={sectionMenus.advisedCriticalHistory} isExpanded={expanded} />
          <AppointmentHistory appointments={appointments} sectionMenu={sectionMenus.appointmentHistory} isExpanded={expanded} />
          <AppointmentHistory appointments={holdingAppointments} sectionMenu={sectionMenus.holdingAppointmentHistory} isExpanded={expanded} holdingHistory />
          <Tyres
            car={car}
            appointment_id={latestAppointment?.id}
            appointment_status_identifier={latestAppointment?.appointment_status_identifier}
            sectionMenu={sectionMenus.tyres}
            isExpanded={expanded}
            showCard
            canDeleteStorage
          />
          <CarDrivingTrend appointments={appointments} sectionMenu={sectionMenus.drivingTrend} isExpanded={expanded} />
        </div>
      </div>
    </>
  );
};
